import { FundDisplay } from './fund-switcher.component';

/**
 * Raw fund object structure from the backend API
 */
export interface RawFund {
  cseg5: string;
  fundName: string;
  location?: string;
  closingBalance: number;
  availableAmount: number;
  currency: string;
  currencySymbol: string;
  receiveGrantCurrency?: string;
  receiveGrantCurrencySymbol?: string;
  nextRenewDate?: string;
  charityStatus?: string;
  fundNumber: string;
  fundType?: string;
  custEntityPreferredName?: string;
  hasOtherCurrencies?: boolean;
  contactRoleAccessLevel?: string;
  contactRole?: string;
  preferredName?: string;
  currenciesWithAssets?: any;
  hasFundCash?: boolean;
  projects?: RawFund[];
  primaryCurrencyId?: string;
  primaryCurrencySymbol?: string;
}

/**
 * Transforms a raw fund object from the backend into the FundDisplay format
 * used by the fund-switcher component.
 *
 * @param rawFund - The raw fund object from the backend API
 * @param level - The nesting level (0 for parent, 1+ for sub-funds)
 * @param parentId - Optional parent fund ID for hierarchical structure
 * @returns A FundDisplay object
 *
 * @example
 * ```typescript
 * const rawFund = {
 *   cseg5: "1415",
 *   fundName: "Test Charity",
 *   fundNumber: "990041",
 *   closingBalance: 110597.37,
 *   currencySymbol: "USD",
 *   projects: [...]
 * };
 * const fundDisplay = transformFundData(rawFund);
 * ```
 */
export function transformFundData(
  rawFund: RawFund,
  level: number = 0,
  parentId?: string
): FundDisplay {
  return {
    id: rawFund.cseg5,
    name: rawFund.fundName,
    accountNumber: rawFund.fundNumber,
    balance: rawFund.closingBalance,
    currency: rawFund.currencySymbol,
    parentId: parentId,
    level: level,
    data: rawFund,
  };
}

/**
 * Transforms an array of raw fund objects (including nested projects) into a flat
 * array of FundDisplay objects with proper hierarchical structure.
 *
 * This function recursively processes parent funds and their projects, maintaining
 * the parent-child relationship through the level and parentId properties.
 *
 * @param rawFunds - Array of raw fund objects from the backend API
 * @returns A flat array of FundDisplay objects with hierarchical structure
 *
 * @example
 * ```typescript
 * const rawFunds = [
 *   {
 *     cseg5: "1415",
 *     fundName: "Parent Fund",
 *     fundNumber: "990041",
 *     closingBalance: 110597.37,
 *     currencySymbol: "USD",
 *     projects: [
 *       {
 *         cseg5: "1738",
 *         fundName: "Project 1",
 *         fundNumber: "990041CA2",
 *         closingBalance: 4685.45,
 *         currencySymbol: "CAD",
 *         projects: []
 *       }
 *     ]
 *   }
 * ];
 *
 * const funds = transformFundsArray(rawFunds);
 * // Returns:
 * // [
 * //   { id: "1415", name: "Parent Fund", level: 0, ... },
 * //   { id: "1738", name: "Project 1", level: 1, parentId: "1415", ... }
 * // ]
 * ```
 */
export function transformFundsArray(rawFunds: RawFund[]): FundDisplay[] {
  const result: FundDisplay[] = [];

  function processFund(rawFund: RawFund, level: number = 0, parentId?: string) {
    // Add the current fund
    const fundDisplay = transformFundData(rawFund, level, parentId);
    result.push(fundDisplay);

    // Process any sub-funds (projects)
    if (rawFund.projects && rawFund.projects.length > 0) {
      rawFund.projects.forEach((project) => {
        processFund(project, level + 1, rawFund.cseg5);
      });
    }
  }

  // Process all top-level funds
  rawFunds.forEach((fund) => processFund(fund));

  return result;
}
